import React, { useEffect } from 'react';
import { Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    document.title = 'Privacy Policy | Fundora Global';
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#102131] to-[#1a3449] text-[#F7FDFF] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield size={64} className="text-[#CDA259] mx-auto mb-6" />
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {t('privacy.title')}
          </h1>
          <p className="text-xl text-[#F7FDFF]/80 max-w-2xl mx-auto">
            {t('privacy.subtitle')}
          </p>
          <p className="text-sm text-[#F7FDFF]/60 mt-4">
            {t('privacy.effectiveDate')}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              {t('privacy.intro')}
            </p>

            {/* Section 1 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section1Title')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('privacy.section1Text')}
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                {t('privacy.section1List', { returnObjects: true }).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4">
                {t('privacy.section1Footer')}
              </p>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section2Title')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('privacy.section2Text')}
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                {t('privacy.section2List', { returnObjects: true }).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4">
                {t('privacy.section2Footer')}
              </p>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section3Title')}
              </h2>
              <p className="text-gray-600">
                {t('privacy.section3Text')}
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section4Title')}
              </h2>
              <p className="text-gray-600">
                {t('privacy.section4Text')}
              </p>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section5Title')}
              </h2>
              <p className="text-gray-600">
                {t('privacy.section5Text')}
              </p>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section6Title')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('privacy.section6Text')}
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                {t('privacy.section6List', { returnObjects: true }).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4">
                {t('privacy.section6Footer')}
              </p>
              <div className="mt-4 p-4 bg-[#F7FDFF] rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> <a href="mailto:info@fundoraglobal.com" className="text-[#CDA259] hover:underline">info@fundoraglobal.com</a>
                </p>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section7Title')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('privacy.section7Text')}
              </p>
              <p className="text-gray-600">
                <a href="https://www.facebook.com/policy.php" target="_blank" rel="noopener noreferrer" className="text-[#CDA259] hover:underline">
                  https://www.facebook.com/policy.php
                </a>
              </p>
              <p className="text-gray-600 mt-4">
                {t('privacy.section7Footer')}
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <h2 className="text-3xl font-bold text-[#102131] mb-4">
                {t('privacy.section8Title')}
              </h2>
              <p className="text-gray-600">
                {t('privacy.section8Text')}
              </p>
              <p className="text-gray-600 mt-4">
                {t('privacy.section8Footer')}
              </p>
            </div>

            {/* Contact Section */}
            <div className="mt-12 p-6 bg-gradient-to-br from-[#102131] to-[#1a3449] rounded-xl text-center">
              <h3 className="text-2xl font-bold text-[#F7FDFF] mb-4">
                {t('privacy.questionsTitle')}
              </h3>
              <p className="text-[#F7FDFF]/80 mb-6">
                {t('privacy.questionsText')}
              </p>
              <a 
                href="mailto:info@fundoraglobal.com"
                className="inline-block bg-[#CDA259] text-[#102131] px-8 py-3 rounded-lg font-semibold hover:bg-[#CDA259]/90 transition-colors"
              >
                {t('faq.contactUs')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
